/** Open time slots for walk-in / edit forms (15-min grid). */
import {
  DAY_BOARD_SLOT_MINUTES,
  isMinuteBookable,
  localMinutesFromIso,
  minutesToHm,
  parseHmToMinutes,
  resourceOpenWindows,
  timeOffWindowsForLocalDay,
} from './dayBoardLayout';
import type { AppointmentResource } from './types';

export function openSlotOptionsForResource(
  dayOfWeek: number,
  resource: AppointmentResource | null | undefined,
  day?: Date,
  timeOffs?: Array<{ startsAt: string; endsAt: string }>,
): string[] {
  if (!resource) return [];
  const wins = resourceOpenWindows(dayOfWeek, resource.hours);
  if (wins.length === 0) return [];
  const blocked = day && timeOffs?.length ? timeOffWindowsForLocalDay(day, timeOffs) : [];
  const out: string[] = [];
  for (const w of wins) {
    for (let m = w.startMin; m < w.endMin; m += DAY_BOARD_SLOT_MINUTES) {
      if (isMinuteBookable(m, wins, blocked)) out.push(minutesToHm(m));
    }
  }
  return out;
}

/**
 * Remove slots that would overlap an existing appointment [start, end) on the
 * same resource and same local day. `durationMinutes` is the selected service
 * duration; a slot at minute M is occupied if [M, M+duration) intersects any
 * existing appointment. `excludeId` skips the appointment being edited.
 */
export function filterSlotsByAppointments(
  slots: string[],
  appointments: Array<{ id: string; resourceId: string; startsAt: string; endsAt: string; status: string }>,
  resourceId: string | null | undefined,
  durationMinutes: number,
  excludeId?: string | null,
): string[] {
  if (!resourceId || appointments.length === 0) return slots;
  const occupied = appointments
    .filter(
      (a) =>
        a.resourceId === resourceId &&
        a.id !== excludeId &&
        a.status !== 'cancelled' &&
        a.status !== 'no_show',
    )
    .map((a) => ({ start: localMinutesFromIso(a.startsAt), end: localMinutesFromIso(a.endsAt) }));
  if (occupied.length === 0) return slots;
  return slots.filter((hm) => {
    const s = parseHmToMinutes(hm);
    const e = s + durationMinutes;
    return !occupied.some((o) => s < o.end && e > o.start);
  });
}

/**
 * Modo mover (proto agenda-ux-v1): start-minutes donde el turno entra completo —
 * [m, m+duration) dentro de una ventana abierta, sin ausencia ni solape.
 * `excludeId` ignora el turno que se está moviendo.
 */
export function moveTargetMinutesForResource(args: {
  dayOfWeek: number;
  resource: AppointmentResource | null | undefined;
  day: Date;
  timeOffs?: Array<{ startsAt: string; endsAt: string }>;
  appointments: Array<{
    id: string;
    resourceId: string;
    startsAt: string;
    endsAt: string;
    status: string;
  }>;
  durationMinutes: number;
  excludeId?: string | null;
}): number[] {
  const { resource, durationMinutes } = args;
  if (!resource || durationMinutes <= 0) return [];
  const openWins = resourceOpenWindows(args.dayOfWeek, resource.hours);
  if (openWins.length === 0) return [];
  const blocked = timeOffWindowsForLocalDay(args.day, args.timeOffs ?? []);
  const day = args.day;
  const sameDay = (iso: string) => {
    const d = new Date(iso);
    return (
      d.getFullYear() === day.getFullYear() &&
      d.getMonth() === day.getMonth() &&
      d.getDate() === day.getDate()
    );
  };
  const occupied = args.appointments
    .filter(
      (a) =>
        a.resourceId === resource.id &&
        a.id !== args.excludeId &&
        a.status !== 'cancelled' &&
        a.status !== 'no_show' &&
        sameDay(a.startsAt),
    )
    .map((a) => ({ start: localMinutesFromIso(a.startsAt), end: localMinutesFromIso(a.endsAt) }));
  const out: number[] = [];
  for (const w of openWins) {
    for (let m = w.startMin; m + durationMinutes <= w.endMin; m += DAY_BOARD_SLOT_MINUTES) {
      const e = m + durationMinutes;
      if (blocked.some((b) => m < b.endMin && e > b.startMin)) continue;
      if (occupied.some((o) => m < o.end && e > o.start)) continue;
      out.push(m);
    }
  }
  return out;
}

/**
 * First open slot after `fromDay` (exclusive), scanning day by day with the
 * same rules as the form picker (open hours + time-off + appointment overlap).
 * Backs the "Próximo disponible" hint when the picked day has no free slots.
 */
export function findNextOpenSlot(args: {
  fromDay: Date;
  daysAhead: number;
  resource: AppointmentResource | null | undefined;
  timeOffs?: Array<{ resourceId: string; startsAt: string; endsAt: string }>;
  appointments: Array<{
    id: string;
    resourceId: string;
    startsAt: string;
    endsAt: string;
    status: string;
  }>;
  durationMinutes: number;
  excludeId?: string | null;
}): { day: Date; hm: string } | null {
  const resource = args.resource;
  if (!resource || args.daysAhead <= 0) return null;
  const duration = args.durationMinutes > 0 ? args.durationMinutes : DAY_BOARD_SLOT_MINUTES;
  const offs = (args.timeOffs ?? []).filter((t) => t.resourceId === resource.id);
  for (let i = 1; i <= args.daysAhead; i++) {
    const day = new Date(
      args.fromDay.getFullYear(),
      args.fromDay.getMonth(),
      args.fromDay.getDate() + i,
    );
    const open = openSlotOptionsForResource(day.getDay(), resource, day, offs);
    if (open.length === 0) continue;
    const dayAppointments = args.appointments.filter((a) => {
      const ad = new Date(a.startsAt);
      return (
        ad.getFullYear() === day.getFullYear() &&
        ad.getMonth() === day.getMonth() &&
        ad.getDate() === day.getDate()
      );
    });
    const free = filterSlotsByAppointments(
      open,
      dayAppointments,
      resource.id,
      duration,
      args.excludeId,
    );
    if (free.length > 0) return { day, hm: free[0]! };
  }
  return null;
}

/** Snap typed HH:mm to nearest open slot, or keep if already open. */
export function snapToOpenSlot(hm: string, options: string[]): string {
  if (options.includes(hm)) return hm;
  if (options.length === 0) return hm;
  const parsed = parseHmToMinutes(hm);
  let best = options[0];
  let bestDist = Infinity;
  for (const opt of options) {
    const d = Math.abs(parseHmToMinutes(opt) - parsed);
    if (d < bestDist) {
      bestDist = d;
      best = opt;
    }
  }
  return best;
}
